import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MdSearch, MdShoppingCart, MdFavoriteBorder, MdPerson, MdMenu, MdClose } from 'react-icons/md';
import { useCartStore } from '../store/cartStore';
import { useAuthStore } from '../store/authStore';
import { useWishlistStore } from '../store/wishlistStore';
import { useAuthModalStore } from '../store/authModalStore';
import ProtectedLink from './ProtectedLink';
import logo from '../assets/logo.png';
import '../styles/Navbar.css';

const Navbar: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const cartItemCount = useCartStore((state) => state.getItemCount());
  const wishlistCount = useWishlistStore((state) => state.items.length);
  const { user, isAuthenticated, logout } = useAuthStore();
  const { openLogin, openRegister } = useAuthModalStore();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (trimmed) {
      navigate(`/products?search=${encodeURIComponent(trimmed)}`);
      setSearchQuery('');
      setMobileMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    setUserDropdownOpen(false);
    setMobileMenuOpen(false);
    navigate('/');
  };

  return (
    <nav className="navbar">
      <div className="navbar__container">
        {/* Logo */}
        <Link to="/" className="navbar__logo" onClick={() => setMobileMenuOpen(false)}>
          <img src={logo} alt="VIN Pulse OS" className="navbar__logo-img" />
        </Link>

        {/* Nav Links - desktop */}
        <ul className="navbar__nav">
          <li><Link to="/" className="navbar__nav-link">Home</Link></li>
          <li><ProtectedLink to="/products" className="navbar__nav-link">Products</ProtectedLink></li>
          <li><Link to="/about" className="navbar__nav-link">About Us</Link></li>
          <li><Link to="/contact" className="navbar__nav-link">Contact Us</Link></li>
        </ul>

        {/* Search - desktop */}
        <form className="navbar__search" onSubmit={handleSearch}>
          <MdSearch className="navbar__search-icon" />
          <input
            type="text"
            className="navbar__search-input"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </form>

        {/* Actions - desktop */}
        <div className="navbar__actions">
          <Link to="/cart" className="navbar__icon-btn" aria-label="Cart">
            <MdShoppingCart />
            {cartItemCount > 0 && <span className="navbar__badge">{cartItemCount}</span>}
          </Link>

          {isAuthenticated && user ? (
            <div className="navbar__user" ref={dropdownRef}>
              <button
                className="navbar__user-btn"
                onClick={() => setUserDropdownOpen((prev) => !prev)}
                aria-label="User menu"
              >
                {user.avatar ? (
                  <img src={user.avatar} alt={user.firstName} className="navbar__avatar" />
                ) : (
                  <MdPerson />
                )}
              </button>

              {userDropdownOpen && (
                <div className="navbar__dropdown">
                  <div className="navbar__dropdown-header">
                    <span className="navbar__dropdown-name">
                      {user.firstName} {user.lastName}
                    </span>
                    <span className="navbar__dropdown-email">{user.email}</span>
                  </div>
                  <div className="navbar__dropdown-divider" />
                  <Link
                    to="/profile"
                    className="navbar__dropdown-item"
                    onClick={() => setUserDropdownOpen(false)}
                  >
                    My Profile
                  </Link>
                  <Link
                    to="/orders"
                    className="navbar__dropdown-item"
                    onClick={() => setUserDropdownOpen(false)}
                  >
                    My Orders
                  </Link>
                  <Link
                    to="/invoices"
                    className="navbar__dropdown-item"
                    onClick={() => setUserDropdownOpen(false)}
                  >
                    My Invoices
                  </Link>
                  <Link
                    to="/returns"
                    className="navbar__dropdown-item"
                    onClick={() => setUserDropdownOpen(false)}
                  >
                    Returns &amp; Disputes
                  </Link>
                  <Link
                    to="/report-issue"
                    className="navbar__dropdown-item"
                    onClick={() => setUserDropdownOpen(false)}
                  >
                    Report an Issue
                  </Link>
                  <div className="navbar__dropdown-divider" />
                  <button className="navbar__dropdown-item navbar__dropdown-logout" onClick={handleLogout}>
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <button className="navbar__login-btn" onClick={openLogin}>
                Sign In
              </button>
              <button className="navbar__partner-btn" onClick={openRegister}>
                Become a VIN Partner
              </button>
            </>
          )}
        </div>

        {/* Hamburger */}
        <button
          className="navbar__hamburger"
          onClick={() => setMobileMenuOpen((prev) => !prev)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <MdClose /> : <MdMenu />}
        </button>
      </div>

      {/* Mobile menu */}
      <div className={`navbar__mobile ${mobileMenuOpen ? 'navbar__mobile--open' : ''}`}>
        <form className="navbar__mobile-search" onSubmit={handleSearch}>
          <MdSearch className="navbar__search-icon" />
          <input
            type="text"
            className="navbar__search-input"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </form>

        <div className="navbar__mobile-links">
          <Link to="/" className="navbar__mobile-link" onClick={() => setMobileMenuOpen(false)}>
            <span>Home</span>
          </Link>
          <ProtectedLink to="/products" className="navbar__mobile-link" onClick={() => setMobileMenuOpen(false)}>
            <span>Products</span>
          </ProtectedLink>
          <Link to="/about" className="navbar__mobile-link" onClick={() => setMobileMenuOpen(false)}>
            <span>About Us</span>
          </Link>
          <Link to="/contact" className="navbar__mobile-link" onClick={() => setMobileMenuOpen(false)}>
            <span>Contact Us</span>
          </Link>

          <div className="navbar__mobile-divider" />

          <Link to="/wishlist" className="navbar__mobile-link" onClick={() => setMobileMenuOpen(false)}>
            <MdFavoriteBorder />
            <span>Wishlist</span>
            {wishlistCount > 0 && <span className="navbar__badge navbar__badge--inline">{wishlistCount}</span>}
          </Link>

          <Link to="/cart" className="navbar__mobile-link" onClick={() => setMobileMenuOpen(false)}>
            <MdShoppingCart />
            <span>Cart</span>
            {cartItemCount > 0 && <span className="navbar__badge navbar__badge--inline">{cartItemCount}</span>}
          </Link>

          {isAuthenticated && user ? (
            <>
              <Link to="/profile" className="navbar__mobile-link" onClick={() => setMobileMenuOpen(false)}>
                <MdPerson />
                <span>My Profile</span>
              </Link>
              <Link to="/orders" className="navbar__mobile-link" onClick={() => setMobileMenuOpen(false)}>
                <MdShoppingCart />
                <span>My Orders</span>
              </Link>
              <button className="navbar__mobile-link navbar__mobile-logout" onClick={handleLogout}>
                Logout
              </button>
            </>
          ) : (
            <button className="navbar__mobile-link navbar__mobile-signin" onClick={() => { setMobileMenuOpen(false); openLogin(); }}>
              <MdPerson />
              <span>Sign In</span>
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
